
import React from 'react';
import { FileCheck, Clock, User, MessageSquare, Plus, Edit, Phone, Mail, CheckCircle, AlertCircle, PhoneCall, PhoneMissed, PhoneIncoming, Send, Voicemail } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";

interface ActivityItemProps {
  icon: React.ReactNode;
  text: string;
  time: string;
  type: 'completed' | 'update' | 'pending';
  status?: string;
  activityType?: string;
}

interface DashboardActivityItem {
  id: string;
  type: 'completed' | 'update' | 'pending';
  title: string;
  description: string;
  time: string;
}

interface ActivityFeedProps {
  items?: DashboardActivityItem[];
  className?: string;
}

const getActivityIcon = (title: string, activityType?: string, status?: string) => {
  // Extract activity type from title if not provided
  const type = activityType || title.toLowerCase();
  
  // Handle specific call statuses with appropriate icons
  if (type.includes('call')) {
    if (status === 'completed') return <PhoneCall className="h-4 w-4" />;
    if (status === 'cancelled' || status === 'no_answer') return <PhoneMissed className="h-4 w-4" />;
    if (status === 'voice_mail' || status === 'voicemail') return <Voicemail className="h-4 w-4" />;
    return <Phone className="h-4 w-4" />;
  }
  
  // Handle email activities
  if (type.includes('email') || type.includes('message')) {
    if (status === 'completed') return <Send className="h-4 w-4" />;
    return <Mail className="h-4 w-4" />;
  }
  
  // Handle SMS
  if (type.includes('sms')) {
    return <MessageSquare className="h-4 w-4" />;
  }
  
  // Handle meetings and scheduling
  if (type.includes('meeting') || type.includes('schedule')) {
    return <Clock className="h-4 w-4" />;
  }
  
  // Handle contacts and users
  if (type.includes('contact') || type.includes('user')) {
    return <User className="h-4 w-4" />;
  }
  
  // Handle leads
  if (type.includes('lead') || type.includes('added')) {
    return <Plus className="h-4 w-4" />;
  }
  
  // Handle updates and edits
  if (type.includes('update') || type.includes('edit')) {
    return <Edit className="h-4 w-4" />;
  }
  
  // Default icons based on status
  if (status === 'completed') return <CheckCircle className="h-4 w-4" />;
  if (status === 'cancelled' || status === 'pending') return <AlertCircle className="h-4 w-4" />;
  
  return <FileCheck className="h-4 w-4" />;
};

const getActivityColor = (type: 'completed' | 'update' | 'pending') => {
  switch (type) {
    case 'completed':
      return 'text-green-600 bg-green-50 dark:bg-green-950/20';
    case 'pending':
      return 'text-orange-600 bg-orange-50 dark:bg-orange-950/20';
    case 'update':
      return 'text-blue-600 bg-blue-50 dark:bg-blue-950/20';
    default:
      return 'text-gray-600 bg-gray-50 dark:bg-gray-950/20';
  }
};

const getStatusBadge = (status?: string) => {
  if (!status) return null;
  
  const statusVariants: { [key: string]: string } = {
    completed: 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400',
    cancelled: 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400',
    voice_mail: 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400',
    voicemail: 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400',
    no_answer: 'bg-orange-100 text-orange-800 dark:bg-orange-900/20 dark:text-orange-400',
    gk: 'bg-purple-100 text-purple-800 dark:bg-purple-900/20 dark:text-purple-400',
    pending: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400'
  };

  const variant = statusVariants[status] || 'bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-400';

  return (
    <Badge variant="secondary" className={`${variant} text-xs px-2 py-0.5`}>
      {status.replace('_', ' ').charAt(0).toUpperCase() + status.replace('_', ' ').slice(1)}
    </Badge>
  );
};

const ActivityItem: React.FC<ActivityItemProps> = ({ icon, text, time, type, status, activityType }) => {
  // Extract actual status from title if available
  const actualStatus = status || (text.includes(' - ') ? text.split(' - ')[1] : undefined);
  
  return (
    <div className="group flex items-start gap-3 p-3 rounded-lg hover:bg-muted/50 transition-colors cursor-pointer">
      <div className={`flex-shrink-0 p-2 rounded-full ${getActivityColor(type)}`}>
        {icon}
      </div>
      <div className="flex-1 min-w-0 space-y-1">
        <div className="flex items-start justify-between gap-2">
          <p className="text-sm font-medium text-foreground leading-tight">
            {text.includes(' - ') ? text.split(' - ')[0] : text}
          </p>
          {getStatusBadge(actualStatus)}
        </div>
        <p className="text-xs text-muted-foreground">{time}</p>
      </div>
    </div>
  );
};

const ActivityFeed: React.FC<ActivityFeedProps> = ({ items = [], className }) => {
  // Default activities as fallback when no real data is available
  const defaultActivities = [
    { 
      id: '1',
      type: 'pending' as const,
      title: "No recent activities",
      description: "Your activities will appear here",
      time: "Start working to see activities"
    }
  ];

  const activitiesToShow = items.length > 0 ? items : defaultActivities;

  return (
    <Card className={`border-0 bg-gradient-to-br from-background to-muted/30 ${className || ''}`}>
      <CardHeader className="pb-2">
        <CardTitle className="text-lg flex items-center gap-2">
          <CheckCircle className="h-5 w-5 text-primary" />
          Activity Feed
        </CardTitle>
        <CardDescription>Recent activities and updates across your workspace</CardDescription>
      </CardHeader>
      <CardContent className="p-0">
        <ScrollArea className="h-[400px] px-4 pb-4">
          <div className="grid grid-cols-2 gap-2">
            {activitiesToShow.map((activity) => {
              const activityType = activity.title.includes(' - ') ? activity.title.split(' - ')[0].toLowerCase() : '';
              const status = activity.title.includes(' - ') ? activity.title.split(' - ')[1] : undefined;
              
              return (
                <ActivityItem 
                  key={activity.id}
                  icon={getActivityIcon(activity.title, activityType, status)}
                  text={activity.title}
                  time={activity.time}
                  type={activity.type}
                  status={status}
                  activityType={activityType}
                />
              );
            })}
          </div>
          
          {activitiesToShow.length === 0 && (
            <div className="flex flex-col items-center justify-center py-8 text-center">
              <CheckCircle className="h-12 w-12 text-muted-foreground/50 mb-3" />
              <h3 className="text-sm font-medium text-muted-foreground">No recent activity</h3>
              <p className="text-xs text-muted-foreground/80 mt-1">
                Your activities will appear here as you work
              </p>
            </div>
          )}
        </ScrollArea>
      </CardContent>
    </Card>
  );
};

export default ActivityFeed;
